import axios from 'axios';
import { getDevLoginToken } from '@/components/auth/DevLoginButton';

/**
 * API Base URL 配置
 * - 开发环境: /api (通过 Vite 代理到 localhost:3001)
 * - 生产环境: https://api.studyjoyful.com/api
 */
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

/**
 * API Client 配置
 * 基础 Axios 实例，用于所有 API 请求
 *
 * 认证 token 会通过 AuthSetup 组件自动注入
 * 或在 Dev Login 模式下使用 dev token
 */
export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// ============================================
// Token Getter 机制
// ============================================

type TokenGetter = () => Promise<string | null>;
let tokenGetter: TokenGetter | null = null;

/**
 * 设置 token getter 函数
 * 由 AuthProvider 组件在 Auth0Provider 内部调用
 */
export const setTokenGetter = (getter: TokenGetter | null) => {
  tokenGetter = getter;
};

/**
 * 获取当前 token (用于非 Axios 场景，如 SSE fetch)
 *
 * 优先级：
 * 1. Dev Login Token（如果启用）
 * 2. Auth0 Access Token（正常认证）
 */
export const getAuthToken = async (): Promise<string | null> => {
  // 优先使用 Dev Login Token
  const devToken = getDevLoginToken();
  if (devToken) {
    return devToken;
  }

  // 使用 Auth0 Access Token
  if (!tokenGetter) return null;
  return tokenGetter();
};

// ============================================
// 请求拦截器 - 自动添加认证 token
// ============================================

apiClient.interceptors.request.use(
  async (config) => {
    // 优先使用 Dev Login Token
    const devToken = getDevLoginToken();
    if (devToken) {
      config.headers.Authorization = `Bearer ${devToken}`;
      return config;
    }

    // 使用 Auth0 Access Token
    if (tokenGetter) {
      try {
        const token = await tokenGetter();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      } catch (error) {
        console.warn('Failed to get auth token:', error);
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// ============================================
// 响应拦截器 - 统一错误处理
// ============================================

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      const { status, data } = error.response;
      console.error(`API Error [${status}]:`, data);

      // 401 错误不在这里处理，由 AuthGuard 处理跳转
    } else if (error.request) {
      console.error('Network Error:', error.message);
    } else {
      console.error('Error:', error.message);
    }

    return Promise.reject(error);
  }
);
